import { ArrowPathIcon, ArrowsRightLeftIcon, Square2StackIcon } from '@heroicons/react/24/outline';
import { clsx } from 'clsx';
import { useId } from 'react';

import { Button } from '@/components/core/Button';
import { Checkbox } from '@/components/core/Checkbox';
import { Icon } from '@/components/core/Icon';
import { useTranslation } from '@/hooks/useTranslation';
import { type CopyCompareAction } from '@/pages/browse/hooks/useCopyCompareActions';
import { selectAllLabel } from '@/pages/browse/toolbar-actions';
import { cn } from '@/services/utils';

// `cn` (tailwind-merge) drops `tw-text-t-sm` next to `tw-text-t-primary`.
const LABEL_CLASS_NAME = 'tw-text-t-sm tw-text-t-primary lg:tw-text-t-base';

export interface BrowseSectionHeaderProps {
    /** Global select-all across the whole result set. */
    allItemsSelected: boolean;
    totalCount: number;
    overCountLimit: boolean;
    countLimit: number;
    onSelectAll: () => void;
    onClearSelection: () => void;
    bulkActionsAlwaysVisible: boolean;
    onBulkActionsAlwaysVisibleChange: (visible: boolean) => void;
    copy: CopyCompareAction;
    compare: CopyCompareAction;
    /** Loads the exact total. The icon shows past the count limit, but not in global mode. */
    onRefreshCount?: () => void;
    countingItems?: boolean;
    /** The exact total has been loaded. The icon stays, disabled. */
    countRecalculated?: boolean;
}

/**
 * Section header above the browse hit list: Select all (Clear selection in
 * global mode) on the left, Bulk actions always visible, Copy and Compare on
 * the right. Stacks on sm in the order Bulk actions, Copy and Compare,
 * Select all. The Select all label truncates first so the checkbox and the
 * recalculation icon stay visible.
 */
export function BrowseSectionHeader({
    allItemsSelected,
    totalCount,
    overCountLimit,
    countLimit,
    onSelectAll,
    onClearSelection,
    bulkActionsAlwaysVisible,
    onBulkActionsAlwaysVisibleChange,
    copy,
    compare,
    onRefreshCount,
    countingItems = false,
    countRecalculated = false,
}: BrowseSectionHeaderProps) {
    const { t } = useTranslation();
    const selectAllId = useId();
    const bulkActionsId = useId();

    const selectLabel = allItemsSelected
        ? t('SECTION_HEADER.CLEAR_SELECTION')
        : selectAllLabel(t, { totalCount, overCountLimit, countLimit });
    const selectAllDisabled = !allItemsSelected && totalCount <= 0;
    const showRefresh = Boolean(onRefreshCount)
        && !allItemsSelected
        && (overCountLimit || countingItems || countRecalculated);
    const refreshDisabled = countingItems || countRecalculated;

    return (
        <div
            id="browse-section-header"
            className="tw-mb-3 tw-flex tw-flex-col tw-items-start tw-gap-4 md:tw-flex-row md:tw-items-center"
        >
            <div className="tw-order-last tw-flex tw-min-w-0 tw-max-w-full tw-items-center tw-gap-3 md:tw-order-none md:tw-flex-1">
                <Checkbox
                    id={selectAllId}
                    isChecked={allItemsSelected}
                    disabled={selectAllDisabled}
                    onCheckedChange={() => (allItemsSelected ? onClearSelection() : onSelectAll())}
                />
                <label
                    htmlFor={selectAllId}
                    className={clsx('tw-min-w-0 tw-truncate', LABEL_CLASS_NAME, !selectAllDisabled && 'tw-cursor-pointer')}
                    title={selectLabel}
                >
                    {selectLabel}
                </label>
                {showRefresh && (
                    <button
                        type="button"
                        onClick={onRefreshCount}
                        disabled={refreshDisabled}
                        aria-busy={countingItems || undefined}
                        className="tw-inline-flex tw-shrink-0 tw-border-0 tw-bg-transparent tw-p-0 disabled:tw-cursor-default"
                        aria-label={t('ITEM.RECALCULATE_ENTRIES')}
                        title={t('ITEM.RECALCULATE_ENTRIES')}
                    >
                        <Icon
                            icon={ArrowPathIcon}
                            disabled={countRecalculated}
                            className={cn(
                                'tw-h-5 tw-w-5 lg:tw-h-6 lg:tw-w-6',
                                countRecalculated ? 'tw-icon-inactive' : 'tw-icon-primary',
                                countingItems && 'tw-animate-spin',
                            )}
                        />
                    </button>
                )}
            </div>
            <div className="tw-flex tw-w-full tw-flex-col tw-items-start tw-gap-4 md:tw-w-auto md:tw-flex-row md:tw-items-center md:tw-justify-end">
                <div className="tw-flex tw-items-center tw-gap-3">
                    <Checkbox
                        id={bulkActionsId}
                        isChecked={bulkActionsAlwaysVisible}
                        onCheckedChange={(checked) => onBulkActionsAlwaysVisibleChange(checked === true)}
                    />
                    <label
                        htmlFor={bulkActionsId}
                        className={clsx('tw-cursor-pointer tw-whitespace-nowrap', LABEL_CLASS_NAME)}
                    >
                        {t('SECTION_HEADER.BULK_ACTIONS_ALWAYS_VISIBLE')}
                    </label>
                </div>
                <div className="tw-flex tw-items-center tw-gap-3">
                    {copy.visible && (
                        <Button
                            id="button-copy-item"
                            variant="outline"
                            hasIcon
                            icon={Square2StackIcon}
                            disabled={copy.disabled}
                            onClick={copy.onClick}
                        >
                            {t('SECTION_HEADER.COPY')}
                        </Button>
                    )}
                    {compare.visible && (
                        <Button
                            id="button-compare"
                            variant="outline"
                            hasIcon
                            icon={ArrowsRightLeftIcon}
                            disabled={compare.disabled}
                            onClick={compare.onClick}
                        >
                            {t('SECTION_HEADER.COMPARE')}
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
}
